import type { ListResult, Params } from '@codes/shared';
import { webCore } from '@codes/web-core';

import type { CreateKeyDTO, KeyView, UpdateKeyDTO, ValidatedUserProfile } from '../types';

const OPENAPI_KEYS_ENDPOINT = (() => {
    const endpoint = import.meta.env.VITE_OPENAPI_API_ENDPOINT;
    if (!endpoint) {
        throw new Error('VITE_OPENAPI_API_ENDPOINT environment variable is not defined');
    }
    return endpoint.toLowerCase();
})();

/**
 * Fetch list of OpenAPI keys with optional query parameters
 */
export const fetchKeys = async (params?: Params): Promise<ListResult<KeyView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${OPENAPI_KEYS_ENDPOINT}/_keys/0/list`,
        })
        .setParams(params || {})
        .execute<ListResult<KeyView>>();

    return data;
};

/**
 * Fetch a single OpenAPI key by ID
 */
export const fetchKeyById = async (keyId: string): Promise<KeyView> => {
    if (!keyId) {
        throw new Error('fetchKeyById: @keyId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${OPENAPI_KEYS_ENDPOINT}/_keys/${keyId}`,
        })
        .execute<KeyView>();

    return data;
};

/**
 * Create a new OpenAPI key
 */
export const createKey = async (createBody: CreateKeyDTO, mocks = false): Promise<KeyView> => {
    const response = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${OPENAPI_KEYS_ENDPOINT}/_keys/0`,
        })
        .setParams({ mocks })
        .setBody(createBody)
        .execute<KeyView>();

    return response.data;
};

/**
 * Update an existing OpenAPI key
 */
export const updateKey = async (updateBody: UpdateKeyDTO): Promise<KeyView> => {
    const { keyId, ...data } = updateBody;
    if (!keyId) {
        throw new Error('updateKey: @keyId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${OPENAPI_KEYS_ENDPOINT}/_keys/${keyId}`,
        })
        .setBody(data)
        .execute<KeyView>();

    return response.data;
};

/**
 * Delete an OpenAPI key
 */
export const deleteKey = async (keyId: string): Promise<KeyView> => {
    if (!keyId) {
        throw new Error('deleteKey: @keyId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${OPENAPI_KEYS_ENDPOINT}/_keys/${keyId}`,
        })
        .execute<KeyView>();

    return data;
};

/**
 * Regenerate an OpenAPI key (revokes old key and creates new one)
 */
export const regenerateKey = async (keyId: string): Promise<KeyView> => {
    if (!keyId) {
        throw new Error('regenerateKey: @keyId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${OPENAPI_KEYS_ENDPOINT}/_keys/${keyId}/regenerate`,
        })
        .execute<KeyView>();

    return data;
};

/**
 * Validate an API key and retrieve associated user profile
 * GET /codes/0/profile with 'x-api-key: <key>' header
 *
 * @param apiKey - The API key to validate
 * @returns ValidatedUserProfile data if valid
 * @throws Error if key is invalid or request fails
 */
export const validateApiKey = async (apiKey: string): Promise<ValidatedUserProfile> => {
    if (!apiKey?.trim()) {
        throw new Error('validateApiKey: @apiKey is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${OPENAPI_KEYS_ENDPOINT}/codes/0/profile`,
        })
        .setHeaders({
            'x-api-key': apiKey.trim(),
        })
        .execute<ValidatedUserProfile>();

    return data;
};
